import {
  BigTitle,
  Box,
  ButtonLink,
  Header,
  InputButton,
  Subtitle,
  Video,
  VideoImage,
  Section,
} from '..'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { KillerArgument } from './Header'
import { globalHistory as history } from '@gatsbyjs/reach-router'
import { EBoxAlign, ETextAlign } from '../type'
import { Colors } from 'src/styles/type'

interface ButtonProps {
  href: string
  text: string
  target?: string | null
  nofollow?: string | null
  theme?: string
}

interface HeaderVideoProps {
  title: string
  subTitle?: string
  killerArgument?: string | null
  image: { src: string; alt: string }
  videoId?: string
  buttons?: ButtonProps[]
  field?: boolean
  classNameForm?: string
  translations?: Record<string, string>
  backgroundColor?: keyof Colors
}

/**
 * Component
 */
const HeaderVideo: React.FC<HeaderVideoProps> = ({
  title,
  subTitle,
  killerArgument = null,
  image,
  videoId,
  buttons = [
    {
      href: 'https://app.assoconnect.com/sign-up/first?subscription=SERENITE',
      text: 'Je commence un essai',
      target: null,
      nofollow: null,
    },
  ],
  field,
  classNameForm,
  translations,
  backgroundColor = 'blue',
}) => {
  const [wistiaPopoverVisible, setWistiaPopoverVisible] = useState(false)

  const { location } = history
  const headerAutoplay = new URLSearchParams(location.search).get(
    'headerAutoplay'
  )

  /**
   * Auto play the video if a headerAutoplay variable exists in the URL
   */
  useEffect(() => {
    if (headerAutoplay) {
      setWistiaPopoverVisible(true)
    }
  }, [headerAutoplay])

  /**
   * Handle click events on VideoButtonWrapper component.
   */
  const handleVideoButtonWrapperClick = () => {
    const existingScript = document.getElementById('wistia')

    if (!existingScript) {
      const wistiaScript = document.createElement('script')
      wistiaScript.src = 'https://fast.wistia.com/assets/external/E-v1.js'
      wistiaScript.id = 'wistia'
      document.body.appendChild(wistiaScript)
    }

    // necessary to force rerendering of the <Video> component, Wistia "popoverhide" event being unreliable in the context of React
    setWistiaPopoverVisible(false)
    setTimeout(() => {
      setWistiaPopoverVisible(true)
    }, 100)
  }

  return (
    <>
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={`${process.env.GATSBY_CDN_HOST}/${image.src}-mobile.webp`}
          imageSrcSet={`${process.env.GATSBY_CDN_HOST}/${image.src}-mobile-2x.webp x2`}
          media="(max-width: 500px)"
        />
        <link
          rel="preload"
          as="image"
          href={`${process.env.GATSBY_CDN_HOST}/${image.src}.webp`}
          imageSrcSet={`${process.env.GATSBY_CDN_HOST}/${image.src}-2x.webp x2`}
          media="(min-width: 500px)"
        />
      </Helmet>
      <Header translations={translations} backgroundColor={backgroundColor}>
        <Box className="pb-medium" align={EBoxAlign.LEFT} width={1 / 2}>
          <BigTitle className="pt-big" align={ETextAlign.LEFT} tag="h1">
            {title}
          </BigTitle>
          <Subtitle
            className="py-medium"
            align={ETextAlign.LEFT}
            color="white"
            tag="div"
          >
            {subTitle}
          </Subtitle>
          {field ? (
            <InputButton
              className={classNameForm}
              translations={translations}
            />
          ) : (
            <Section
              background={{ color: 'transparent', block: 'transparent' }}
              paddingY="0"
              justifyContent="flex-start"
            >
              {buttons.map((button, index) => (
                <ButtonLink
                  {...(index > 0 && { margin: '0 0 0 1rem' })}
                  key={index}
                  href={button.href}
                  theme={button.theme}
                  target={button.target ?? undefined}
                >
                  {button.text}
                </ButtonLink>
              ))}
            </Section>
          )}
          {killerArgument && <KillerArgument>{killerArgument}</KillerArgument>}
        </Box>
        <Box width={1 / 2}>
          <VideoImage
            image={image}
            onClick={handleVideoButtonWrapperClick}
            videoId={videoId}
            visibleByDefault
          />
        </Box>
      </Header>
      {wistiaPopoverVisible && (
        <Video
          videoId={videoId}
          wistiaParameters={[
            'popover=true',
            'popoverShowOnLoad=true',
            'popoverAnimateThumbnail=true',
          ]}
        />
      )}
    </>
  )
}

export default HeaderVideo
